import React, { useCallback, useEffect, useState } from 'react';

import { Group, Stack, Text } from '@mantine/core';
import { Eventcalendar, MbscCalendarEvent } from '@mobiscroll/react';
import dayjs from 'dayjs';
import useProgramCalendar from 'hooks/program/useProgramCalendar';
import useDebounce from 'hooks/useDebounce';
import { ProgramRetrieve } from 'types';

import AgendaItem from './AgendaItem';
import { AgendaEvent, IProgramCalendarAgendaViewProps } from './interface';
import useProgramAgendaStyles from './styles';

const ProgramCalendarAgendaView = ({
  programCalendarNavigationButtons,
  date,
  setDate,
  searchFilters,
  calendarMaxDate,
  handleEditProgram,
  setProgram,
}: IProgramCalendarAgendaViewProps): JSX.Element => {
  const { classes } = useProgramAgendaStyles();
  const [dateSelected, setDateSelected] = useState<string>(date);
  const [calendarEvents, setCalendarEvents] = useState<MbscCalendarEvent[]>([]);
  const debouncedStart = useDebounce(dateSelected, 200);

  useEffect(() => {
    setDateSelected(date);
  }, [date]);

  const {
    data: programData,
    isError: programIsError,
    isSuccess: programIsSuccess,
  } = useProgramCalendar({
    start: debouncedStart,
    end: dayjs(debouncedStart).add(1, 'day').format('YYYY-MM-DD'),
    ...searchFilters,
  });

  const onPageLoading = (event: {
    firstDay: string | number | Date | dayjs.Dayjs | null | undefined;
  }): void => {
    const newDate = dayjs(event.firstDay).format('YYYY-MM-DD');
    if (newDate !== dateSelected) {
      setDateSelected(newDate);
      setDate(newDate);
    }
  };

  useEffect(() => {
    if (programIsSuccess && programData) {
      if (programData.data.length > 0) {
        const programAgenda = programData.data.map((program: ProgramRetrieve): MbscCalendarEvent => {
          return {
            start: new Date(`${program.date}T00:00`),
            totalPrograms: 1,
            id: program.programId,
            programId: program.programId,
            programDate: program.date,
            workoutNotes: program.workoutNotes,
            kiloClassType: program.kiloClassType,
            programName: program.kiloClassType.name,
            createdAt: program.createdAt,
            updatedAt: program.updatedAt,
            gym: program.gym,
            isProgrammed: program.isProgrammed,
          };
        });
        setCalendarEvents(programAgenda);
      } else if (programData.data.length === 0) {
        setCalendarEvents([]);
      }
    }
  }, [programIsSuccess, programData]);

  const handleEventClick = useCallback(
    (event: { original: AgendaEvent }): void => {
      const programFromEvent = {
        programId: event.original.programId,
        kiloClassType: event.original.kiloClassType,
        workoutNotes: event.original.workoutNotes,
        date: event.original.programDate,
        createdAt: event.original.createdAt,
        updatedAt: event.original.updatedAt,
        gym: event.original.gym,
        isProgrammed: event.original.isProgrammed,
      };

      setProgram(programFromEvent);
      handleEditProgram(event.original.id);
    },
    [handleEditProgram, setProgram],
  );

  const renderAgenda = useCallback(
    (data: any): JSX.Element => {
      if (data.length && data[0].events) {
        const events = data[0].events;
        const agendaItems = events.map((event: { original: AgendaEvent }) => {
          return (
            <AgendaItem
              key={event.original.id}
              event={event.original}
              onClick={() => handleEventClick(event)}
            />
          );
        });

        return (
          <Stack className={classes.agendaContent}>
            <Group className={`${classes.header} ${classes.grid}`}>
              {['Event', 'Programmed'].map(header => (
                <Text c='dimmed' fs='italic' fw='normal' fz={'xs'} key={header}>
                  {header}
                </Text>
              ))}
            </Group>
            {agendaItems}
          </Stack>
        );
      }
      return <></>;
    },
    [classes, handleEventClick],
  );

  if (programIsError) return <>Something went wrong</>;

  return (
    <>
      <Eventcalendar
        renderHeader={programCalendarNavigationButtons}
        view={{ agenda: { type: 'day' } }}
        selectedDate={new Date(`${dateSelected}T00:00`)}
        theme='ios'
        themeVariant='light'
        max={calendarMaxDate}
        data={calendarEvents}
        className={classes.calendar}
        renderAgenda={renderAgenda}
        onPageLoading={onPageLoading}
      />
    </>
  );
};

export default ProgramCalendarAgendaView;
